import { useState } from 'react';
import { isNullOrWhitespace } from '../../../../utils/guard';
import FormGroup from '../../../generic/FormGroup';
import HTMLInput from '../../../generic/HTMLEditor/HTMLInput';
import NameInput from '../common/NameInput';
import NavigationOverrideList from '../common/navigationOverrides/NavigationOverrideList';
import OutcomeList from '../common/outcomes/OutcomeList';
import { useMapElement } from '../contextProviders/MapElementProvider';
import ModalBody from '../../../generic/modal/ModalBody';
import ModalFooter from '../../../generic/modal/ModalFooter';
import type { MapElementRequestAPI } from '../../../../sources/graph';
import Footer from '../common/Footer';

/**
 * The main step config screen
 */
const StepConfiguration = () => {
    const { mapElement, onSaveMapElement, onClose, setMapElement, onUpdateName } = useMapElement();

    const [hasSubmitted, setHasSubmitted] = useState(false);
    const isFormValid = isNullOrWhitespace(mapElement?.developerName) === false;

    const onSave = () => {
        setHasSubmitted(true);

        if (isFormValid) {
            onSaveMapElement(mapElement as MapElementRequestAPI);
        }
    };

    if (!mapElement) {
        onClose();
        return;
    }

    const renderBody = () => (
        <>
            <NameInput
                isValid={isFormValid}
                showValidation={hasSubmitted}
                id="step-name"
                name={mapElement.developerName ?? ''}
                onUpdateName={onUpdateName}
            />
            <FormGroup label="The content to be displayed to users">
                <HTMLInput
                    contentValue={mapElement.userContent ?? ''}
                    onChange={(userContent) =>
                        setMapElement({
                            ...mapElement,
                            userContent,
                        })
                    }
                    hasLinkPlugin={true}
                    hasImagePlugin={true}
                    hasValuePickerPlugin={true}
                    height={240}
                />
            </FormGroup>
            <div className="alert alert-warning">
                {'Script tags are not enabled by default. Learn more in our '}
                <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://help.boomi.com/csh?context=GUID-6c363b3c-9556-4075-8763-7e98308a80b9"
                >
                    Documentation
                </a>
                .
            </div>
            <OutcomeList />
            <NavigationOverrideList />
            <FormGroup
                label="Message to show users that are not authorized to take action"
                labelId="notAuthorizedMessage"
            >
                <HTMLInput
                    contentValue={mapElement.notAuthorizedMessage ?? ''}
                    onChange={(notAuthorizedMessage) =>
                        setMapElement({
                            ...mapElement,
                            notAuthorizedMessage,
                        })
                    }
                    hasLinkPlugin={true}
                    hasImagePlugin={true}
                    hasValuePickerPlugin={true}
                    height={240}
                />
            </FormGroup>
            <FormGroup label="Comments about this Step" htmlFor="comments">
                <textarea
                    id="comments"
                    data-testid="comments"
                    className="form-control form-textarea"
                    value={mapElement.developerSummary ?? ''}
                    onChange={({ target: { value: developerSummary } }) =>
                        setMapElement({
                            ...mapElement,
                            developerSummary,
                        })
                    }
                />
            </FormGroup>
        </>
    );

    const renderFooter = () => (
        <>
            <Footer save={onSave} cancel={onClose} />
        </>
    );

    return (
        <>
            <ModalBody>{renderBody()}</ModalBody>
            <ModalFooter>{renderFooter()}</ModalFooter>
        </>
    );
};

export default StepConfiguration;
